import { useMemo } from "react";
import { Voice } from '@/types/voice/voice';

export type VoiceTab = "recommended" | "favorites" | "all";

interface VoiceFilteringOptions {
  voices?: Voice[];
  favoriteVoices: Voice[];
  recommendedVoices: Voice[];
  allVoices: Voice[];
  selectedTab: VoiceTab;
  searchQuery: string;
  filters: {
    category: string;
    accent: string;
    language: string;
    gender: string;
    age: string;
  };
}

export const useVoiceFiltering = ({
  voices,
  favoriteVoices,
  recommendedVoices,
  allVoices,
  selectedTab,
  searchQuery,
  filters
}: VoiceFilteringOptions) => {
  const hasActiveFilters = filters.category !== "all" || filters.accent !== "all" || filters.gender !== "all" || filters.age !== "all" || filters.language !== "all";

  const filteredVoices = useMemo(() => {
    let voiceList: Voice[] = [];

    switch (selectedTab) {
      case "favorites":
        voiceList = favoriteVoices;
        break;
      case "recommended":
        voiceList = recommendedVoices;
        break;
      case "all":
        voiceList = allVoices || [];
        // Apply filters
        if (filters.category !== "all") {
          voiceList = voiceList.filter(v => v.category?.toLowerCase() === filters.category.toLowerCase());
        }
        if (filters.accent !== "all") {
          voiceList = voiceList.filter(v => v.labels?.accent?.toLowerCase() === filters.accent.toLowerCase());
        }
        if (filters.language !== "all") {
          voiceList = voiceList.filter(v => v.labels?.language?.toLowerCase() === filters.language.toLowerCase());
        }
        if (filters.gender !== "all") {
          voiceList = voiceList.filter(v => v.labels?.gender?.toLowerCase() === filters.gender.toLowerCase());
        }
        if (filters.age !== "all") {
          voiceList = voiceList.filter(v => v.labels?.age?.toLowerCase() === filters.age.toLowerCase());
        }
        break;
      default:
        voiceList = recommendedVoices;
    }

    if (!searchQuery) return voiceList;

    const query = searchQuery.toLowerCase();
    return voiceList.filter(voice =>
      voice.name?.toLowerCase().includes(query) ||
      voice.description?.toLowerCase().includes(query) ||
      voice.category?.toLowerCase().includes(query) ||
      voice.labels?.accent?.toLowerCase().includes(query) ||
      voice.labels?.gender?.toLowerCase().includes(query) ||
      voice.labels?.language?.toLowerCase().includes(query)
    );
  }, [selectedTab, searchQuery, favoriteVoices, recommendedVoices, allVoices, voices, filters, hasActiveFilters]);

  return {
    filteredVoices,
    hasActiveFilters
  };
};
